import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { LocationService } from "./service";
import { CustomerService } from "../customers/service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { usePagination } from "../../shared/hooks/usePagination";
import GooglePlacesAutocomplete from "../../shared/components/GooglePlacesAutocomplete";
import { reverseGeocode, extractPostalCode } from "../../shared/utils/geocoding";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const COORDINATE_REGEX = /^-?\d{1,2}(\.\d{1,15})?,\s*-?\d{1,3}(\.\d{1,15})?$/;
const COORDINATE_ERROR_MESSAGE = "validation.coordinatesValidation";

const schema = yup.object().shape({
  customerId: yup.number().required("validation.required"),
  city: yup.string().optional(),
  address: yup.string().required("validation.required"),
  zip: yup.string().optional(),
  note: yup.string().nullable().optional().max(1000, "validation.maxContent"),
  coordinates: yup
    .string()
    .nullable()
    .optional()
    .test("coordinates-format", COORDINATE_ERROR_MESSAGE, function (value) {
      if (!value || value.trim() === "") {
        return true; // Allow empty values
      }
      return COORDINATE_REGEX.test(value);
    }),
  propertyOwner: yup.string().optional(),
  objectReferenceNumber: yup.string().optional(),
  parkingSpace: yup.boolean().optional(),
  elevator: yup.boolean().optional(),
});

interface LocationState {
  customerId?: number;
  city?: string;
  address?: string;
  zip?: string;
  note?: string;
  coordinates?: string;
  propertyOwner?: string;
  objectReferenceNumber?: string;
  parkingSpace?: boolean;
  elevator?: boolean;
}

const LocationForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [locationData, setLocationData] = useState<LocationState | null>(null);
  const [customerOptions, setCustomerOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<LocationState>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      customerId: undefined,
      city: "",
      address: "",
      zip: "",
      note: "",
      coordinates: "",
      propertyOwner: "",
      objectReferenceNumber: "",
      parkingSpace: false,
      elevator: false,
    },
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);
  const { pagination, setTotalPages, setCount } = usePagination({
    perPage: 25,
  });

  // Fetch customers for select
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await CustomerService.get(pagination);
        setCustomerOptions(
          res.data.map((c: any) => ({
            value: c.id,
            label: c.name,
          })),
        );
      } catch (e) {
        ErrorToast(e);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (isEdit) {
      const fetchLocation = async () => {
        try {
          const response = await LocationService.getSingle(+id!);
          setLocationData({
            customerId: response.customer?.id,
            city: response.city || "",
            address: response.address,
            zip: response.zip || "",
            note: response.note || "",
            coordinates: response.coordinates || "",
            propertyOwner: response.propertyOwner || "",
            objectReferenceNumber: response.objectReferenceNumber || "",
            parkingSpace: response.parkingSpace || false,
            elevator: response.elevator || false,
          });
        } catch (error) {
          ErrorToast(error);
        }
      };
      fetchLocation();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (locationData) {
      reset(locationData);
    }
  }, [locationData, reset]);

  const onSubmit = async (data: LocationState) => {
    try {
      if (isEdit) {
        await LocationService.update(data, +id!);
      } else {
        await LocationService.create(data);
      }
      SuccessToast(t(`location.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate("/locations");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col xl={4}>
                    <Form.Label className="required">
                      {t("location.customer")}
                    </Form.Label>
                    <Controller
                      name="customerId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          classNamePrefix="react-select"
                          className={`react-select-container ${errors.customerId ? "is-invalid" : ""}`}
                          options={customerOptions}
                          value={
                            customerOptions.find(
                              (option) => option.value === field.value,
                            ) || null
                          }
                          onChange={(option) =>
                            field.onChange(option ? option.value : undefined)
                          }
                          placeholder={t("location.selectCustomer")}
                          isClearable
                        />
                      )}
                    />
                    {errors.customerId && (
                      <div className="invalid-feedback">
                        {t("validation.required")}
                      </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="propertyOwner">
                      <Form.Label>{t("general.propertyOwner")}</Form.Label>
                      <Controller
                        control={control}
                        name="propertyOwner"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            isInvalid={!!errors?.propertyOwner?.message?.length}
                            placeholder={t("general.enterPropertyOwner")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.propertyOwner &&
                          t(errors.propertyOwner?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="objectReferenceNumber">
                      <Form.Label>{t("general.objectReferenceNumber")}</Form.Label>
                      <Controller
                        control={control}
                        name="objectReferenceNumber"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            isInvalid={!!errors?.objectReferenceNumber?.message?.length}
                            placeholder={t("general.enterObjectReferenceNumber")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.objectReferenceNumber &&
                          t(errors.objectReferenceNumber?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group controlId="address">
                      <Form.Label className={"required"}>
                        {t("general.address")}
                      </Form.Label>
                      <Controller
                        control={control}
                        name="address"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <GooglePlacesAutocomplete
                            value={value}
                            onChange={onChange}
                            onPlaceSelect={async (place) => {
                              // Auto-fill coordinates if available
                              if (place.coordinates) {
                                setValue(
                                  "coordinates",
                                  `${place.coordinates.lat}, ${place.coordinates.lng}`,
                                );
                              }

                              // Extract address components if available
                              if (place.addressComponents) {
                                // Extract city
                                const cityComponent =
                                  place.addressComponents.find(
                                    (component: any) =>
                                      component.types.includes("locality") ||
                                      component.types.includes(
                                        "administrative_area_level_1",
                                      ) ||
                                      component.types.includes("sublocality"),
                                  );
                                if (cityComponent && cityComponent.long_name) {
                                  setValue("city", cityComponent.long_name);
                                }

                                // Extract ZIP code
                                const zipComponent =
                                  place.addressComponents.find(
                                    (component: any) =>
                                      component.types.includes("postal_code") ||
                                      component.types.includes("postal_code_prefix"),
                                  );
                                if (zipComponent && zipComponent.long_name) {
                                  setValue("zip", zipComponent.long_name);
                                } else if (place.coordinates) {
                                  // Fallback: Do reverse geocoding to get postal code
                                  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                                  if (apiKey) {
                                    try {
                                      const reverseResult = await reverseGeocode(
                                        place.coordinates.lat,
                                        place.coordinates.lng,
                                        apiKey
                                      );
                                      if (reverseResult) {
                                        const postalCode = extractPostalCode(
                                          reverseResult.address_components
                                        );
                                        if (postalCode) {
                                          setValue("zip", postalCode);
                                        } else {
                                          // Last fallback: try to extract ZIP from formatted address
                                          const zipMatch = place.address?.match(/\b(\d{4})\b/);
                                          if (zipMatch && zipMatch[1]) {
                                            setValue("zip", zipMatch[1]);
                                          }
                                        }
                                      }
                                    } catch (error) {
                                      console.error("Reverse geocoding failed:", error);
                                      // Last fallback: try to extract ZIP from formatted address
                                      const zipMatch = place.address?.match(/\b(\d{4})\b/);
                                      if (zipMatch && zipMatch[1]) {
                                        setValue("zip", zipMatch[1]);
                                      }
                                    }
                                  }
                                }
                              }
                            }}
                            placeholder={t("general.enterAddress")}
                            isInvalid={!!errors?.address?.message?.length}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.address && t("validation.required")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="zip">
                      <Form.Label>{t("general.zip")}</Form.Label>
                      <Controller
                        control={control}
                        name="zip"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            isInvalid={!!errors?.zip?.message?.length}
                            placeholder={t("general.enterZip")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.zip && t(errors.zip?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group controlId="city">
                      <Form.Label>{t("general.city")}</Form.Label>
                      <Controller
                        control={control}
                        name="city"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            isInvalid={!!errors?.city?.message?.length}
                            placeholder={t("general.enterCity")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.city && t(errors.city?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {/*<Col md={12}>*/}
                  {/*  <Form.Group className="mb-3" controlId="coordinates">*/}
                  {/*    <Form.Label>{t("general.coordinates")}</Form.Label>*/}
                  {/*    <Controller*/}
                  {/*      control={control}*/}
                  {/*      name="coordinates"*/}
                  {/*      defaultValue=""*/}
                  {/*      render={({ field: { onChange, value } }) => (*/}
                  {/*        <Form.Control*/}
                  {/*          onChange={onChange}*/}
                  {/*          value={value}*/}
                  {/*          isInvalid={!!errors?.coordinates?.message?.length}*/}
                  {/*          placeholder={t("general.enterCoordinates")}*/}
                  {/*        />*/}
                  {/*      )}*/}
                  {/*    />*/}
                  {/*    <Form.Control.Feedback type="invalid">*/}
                  {/*      {errors.coordinates &&*/}
                  {/*        t(errors.coordinates?.message as string)}*/}
                  {/*    </Form.Control.Feedback>*/}
                  {/*  </Form.Group>*/}
                  {/*</Col>*/}

                  <Col md={2}>
                    <Form.Group controlId="parkingSpace">
                      <Form.Label>{t("general.parkingSpace")}</Form.Label>
                      <Controller
                        control={control}
                        name="parkingSpace"
                        render={({ field: { onChange, value } }) => (
                          <Form.Check
                            type={"switch"}
                            onChange={onChange}
                            checked={value || false}
                            isInvalid={!!errors?.parkingSpace?.message?.length}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.parkingSpace &&
                          t(errors.parkingSpace?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId="elevator">
                      <Form.Label>{t("general.elevator")}</Form.Label>
                      <Controller
                        control={control}
                        name="elevator"
                        render={({ field: { onChange, value } }) => (
                          <Form.Check
                            type={"switch"}
                            onChange={onChange}
                            checked={value || false}
                            isInvalid={!!errors?.elevator?.message?.length}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.elevator &&
                          t(errors.elevator?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group controlId="note">
                      <Form.Label>{t("general.note")}</Form.Label>
                      <Controller
                        control={control}
                        name="note"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            as="textarea"
                            rows={3}
                            onChange={onChange}
                            value={value ?? ""}
                            isInvalid={!!errors?.note?.message?.length}
                            placeholder={t("general.enterNote")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.note && t(errors.note?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`location.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/locations")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default LocationForm;
